// src/pages/Tasks/TaskCreate.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksApi, TaskCreatePayload, TaskType } from '../../api/tasks';
import { clientsApi, Client } from '../../api/clients';
import { vendorsApi, Vendor } from '../../api/vendors';
import { projectsApi, Project } from '../../api/projects';
import { getAllUsers } from '../../api/user';
import { format } from "date-fns";
import { useToast } from "../../hooks/useToast";
import { useFileDropPaste } from "../../hooks/useFileDropPaste";
import { useAuth } from "../../contexts/AuthContext";
import { createPortal } from 'react-dom';
// AI helpers
import AiGenerateButton from '../../components/AiGenerateButton';
import ErrorBoundary from '../../components/ErrorBoundary';
import { generateTaskDescription, generateTaskChecklist } from '../../api/taskAi';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { buildRecurrence, computeNextSpawnAt, intervalUnit, type RecurrenceInterval } from '../../utils/recurrence';
import {
  Calendar as CalendarIcon,
  ChevronDown,
  Search,
  X,
} from "lucide-react";
import { Calendar } from "../../components/ui/calendar";
import { DateField } from "../../components/ui/DateField";
import { AttachmentThumb } from "../../components/AttachmentThumb";

type PreparorUser = {
  id?: string;
  user_id?: string;
  name?: string;
  role?: string;
};
function PreparorSelect({
  value,
  onChange,
  options,
  disabled,
  placeholder = "Select Preparor",
}: {
  value: string;
  onChange: (id: string) => void;
  options: PreparorUser[];
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightedIdx, setHighlightedIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = options.find((u) => (u.id || u.user_id) === value);
  const selectedLabel = selected
    ? `${selected.name || ""}${selected.role ? ` (${selected.role})` : ""}`
    : "";

  // Close on outside click. Clear the query so the input reverts to
  // showing the currently selected label (or the placeholder).
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const displayValue = open ? query : selectedLabel;
  const filtered = options.filter((u) => {
    const q = query.trim().toLowerCase();
    if (!q || q === selectedLabel.trim().toLowerCase()) return true;
    const name = (u.name || "").toLowerCase();
    const role = (u.role || "").toLowerCase();
    return name.includes(q) || role.includes(q);
  });

  // Keep the highlight on a valid row whenever the filter changes.
  useEffect(() => {
    setHighlightedIdx((i) =>
      filtered.length === 0 ? 0 : Math.min(i, filtered.length - 1),
    );
  }, [filtered.length]);

  // Scroll the highlighted row into view when arrow-keying through.
  useEffect(() => {
    if (!open) return;
    const ul = listRef.current;
    if (!ul) return;
    const node = ul.querySelector<HTMLLIElement>(
      `li[data-idx="${highlightedIdx}"]`,
    );
    if (node) node.scrollIntoView({ block: "nearest" });
  }, [highlightedIdx, open]);

  const commit = (idx: number) => {
    const opt = filtered[idx];
    if (!opt) return;
    const id = opt.id || opt.user_id || "";
    onChange(id);
    setOpen(false);
    setQuery("");
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setHighlightedIdx((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      // Pick the highlighted match. preventDefault stops the surrounding
      // <form> from submitting when the user hits Enter inside the input.
      e.preventDefault();
      if (open && filtered.length > 0) {
        commit(highlightedIdx);
      } else if (!open) {
        setOpen(true);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setQuery("");
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 ${
          disabled ? "opacity-50" : ""
        }`}
      >
        <Search className="h-3.5 w-3.5 opacity-50 shrink-0 mr-2" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder}
          value={displayValue}
          onChange={(e) => {
            setQuery(e.target.value);
            if (!open) setOpen(true);
            setHighlightedIdx(0);
          }}
          onFocus={() => {
            if (disabled) return;
            setOpen(true);
            // Seed the query with the currently selected label so the
            // user sees the existing pick and can edit / replace it.
            setQuery(selectedLabel);
            // Select all so typing immediately replaces the seed text
            // instead of appending to it.
            requestAnimationFrame(() => inputRef.current?.select());
          }}
          onKeyDown={handleKeyDown}
          className="flex-1 min-w-0 bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        />
        {value && !disabled && (
          <button
            type="button"
            onMouseDown={(e) => {
              // Prevent the input from blurring before our handler runs.
              e.preventDefault();
              onChange("");
              setQuery("");
              setOpen(false);
              inputRef.current?.focus();
            }}
            className="opacity-60 hover:opacity-100 ml-1"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => {
            e.preventDefault();
            if (disabled) return;
            setOpen((o) => !o);
            inputRef.current?.focus();
          }}
          className="ml-1 opacity-60 hover:opacity-100 shrink-0"
          aria-label={open ? "Close suggestions" : "Open suggestions"}
        >
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      {open && (
        <div
          className="absolute left-0 right-0 z-[200] mt-1 bg-white rounded-md border shadow-lg overflow-hidden"
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* ~5 rows visible at a time; rest scrolls internally. */}
          <ul ref={listRef} className="max-h-[220px] overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-xs text-gray-400 italic">
                {query
                  ? <>No users match &ldquo;{query}&rdquo;.</>
                  : "No users available."}
              </li>
            ) : (
              filtered.map((u, idx) => {
                const id = u.id || u.user_id || "";
                const isSelected = id === value;
                const isHighlighted = idx === highlightedIdx;
                return (
                  <li
                    key={id}
                    data-idx={idx}
                    onMouseEnter={() => setHighlightedIdx(idx)}
                    onMouseDown={(e) => {
                      // Prevent the input blur from closing the panel
                      // before our click-handler runs.
                      e.preventDefault();
                      commit(idx);
                    }}
                    className={`px-3 py-1.5 text-sm cursor-pointer ${
                      isHighlighted ? "bg-indigo-50" : "hover:bg-gray-50"
                    } ${isSelected ? "font-medium text-indigo-700" : ""}`}
                  >
                    {u.name} {u.role ? `(${u.role})` : ""}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

interface TaskCreateContext {
  conversationId?: string | null;
  messageId?: string | null;
  initialTitle?: string;
  initialDescription?: string;
  availableUserIds?: string[];
}

/**
 * TaskCreate operates in one of three modes, controlling tab visibility,
 * submission endpoint, and the action buttons offered. Only tab visibility
 * is wired in T20 — endpoint/button wiring follows in T21 / T22.
 *
 *  - 'standard'        : default — full create-task flow (existing behaviour).
 *  - 'cross-org-draft' : external org drafting a task into another head office.
 *                        Approval chain is hidden because the receiving
 *                        head office owns approval setup.
 *  - 'admin-review'    : receiving head-office admin reviews a draft and
 *                        chooses to promote / send back / reject.
 */
export type TaskCreateMode = 'standard' | 'cross-org-draft' | 'admin-review';

interface TaskCreateProps {
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
  onCreated?: (taskId: string) => void;
  chatContext?: TaskCreateContext;
  defaultProjectId?: string;
  defaultClientId?: string;
  mode?: TaskCreateMode;
  /** Required when mode === 'cross-org-draft'. The receiving head office. */
  targetHeadOfficeId?: string;
  /** Required when mode === 'admin-review'. The draft being reviewed. */
  draftTaskId?: string;
}

interface User {
  id: string;
  user_id?: string;
  name: string;
  email: string;
  role?: string;
  department?: string;
  department_id?: string;
  employee_id?: string;
}

interface ApprovalLevel {
  level: number;
  approver: string;
  roleLabel: string;
  status: string;
  actionAt: string | null;
  remark: string;
}

interface PreparorAssignment {
  user_id: string;
  role_in_task: 'PREPAROR';
}

interface TaskTypeWithDefault extends TaskType {
  is_default?: boolean;
}

function buildInitialForm(
  chatContext?: TaskCreateContext,
  creatorId?: string,
): TaskCreatePayload & { approvalChain?: ApprovalLevel[]; recurrence_interval?: RecurrenceInterval; recurrence_every?: number } {
  return {
    title: chatContext?.initialTitle || '',
    description: chatContext?.initialDescription || '',
    task_type: 'GENERAL',
    priority: 'MEDIUM',
    department_id: '',
    // "Task Date" on the form — drives the Daily Planner due_date on the
    // todos auto-created from this task's checklist items. Defaults to
    // today so a freshly opened form already has a sensible value.
    start_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    estimated_hours: undefined,
    is_recurring: false,
    recurrence_interval: 'WEEKLY',
    recurrence_every: 1,
    assignees: [],
    checklist: [''],
    approvalChain: [
      {
        level: 1,
        // Default the first reviewer to the task creator — the user
        // opening this form. The picker stays editable, so they can
        // still swap themselves out before submitting.
        approver: creatorId || '',
        roleLabel: 'Reviewer',
        status: 'Pending',
        actionAt: null,
        remark: '',
      },
    ],
  };
}

function TaskCreate({
  isOpen = true,
  onClose,
  onSuccess,
  onCreated,
  chatContext,
  defaultProjectId,
  defaultClientId,
  mode = 'standard',
  targetHeadOfficeId,
  draftTaskId,
}: TaskCreateProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  // The currently logged-in user — used to default the first approval
  // level's reviewer to the task creator.
  const { user: currentUser } = useAuth();
  const creatorId = (currentUser as any)?.id || (currentUser as any)?.user_id || "";
  const [activeSection, setActiveSection] = useState<'basic' | 'details' | 'approval' | 'checklist' | 'attachments'>('basic');
  // Staged attachments — the task doesn't exist yet, so we hold files in
  // memory and upload them after tasksApi.create() returns the new task id.
  const [stagedAttachments, setStagedAttachments] = useState<File[]>([]);
  const [uploadingAttachments, setUploadingAttachments] = useState(false);
  const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024;
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const formBodyRef = useRef<HTMLDivElement>(null);

  const acceptAttachmentFiles = useCallback((files: File[]) => {
    if (!files.length) return;
    const tooBig = files.filter(f => f.size > ATTACHMENT_MAX_BYTES);
    const ok = files.filter(f => f.size <= ATTACHMENT_MAX_BYTES);
    if (tooBig.length > 0) {
      toast({
        title: 'File too large',
        description: `${tooBig.map(f => f.name).join(', ')} exceeds the 5 MB limit and was skipped.`,
        variant: 'destructive',
      });
    }
    if (ok.length) setStagedAttachments(prev => [...prev, ...ok]);
  }, [ATTACHMENT_MAX_BYTES, toast]);

  const { dropRef: attachmentDropRef, isDragOver: attachmentDragOver } = useFileDropPaste({
    onFiles: acceptAttachmentFiles,
    disabled: activeSection !== 'attachments',
    pasteOnDocument: true,
  });
  const goToSection = (next:
    | 'basic'
    | 'details'
    | 'approval'
    | 'checklist'
    | 'attachments') => {
    setActiveSection(next);
    // Defer to next frame so the new content has mounted before we scroll.
    requestAnimationFrame(() => {
      if (formBodyRef.current) formBodyRef.current.scrollTop = 0;
    });
  };
  // Live preview of the auto-generated task code. Loaded from
  // /tasks/code-settings → settings.next_preview. Read-only on the form.
  const [nextTaskCode, setNextTaskCode] = useState<string | null>(null);
  const [taskTypes, setTaskTypes] = useState<TaskTypeWithDefault[]>([]);
  const [loadingTaskTypes, setLoadingTaskTypes] = useState(false);
  const [showCreateTaskTypeModal, setShowCreateTaskTypeModal] = useState(false);
  const [newTaskTypeName, setNewTaskTypeName] = useState('');
  const [creatingTaskType, setCreatingTaskType] = useState(false);
  const [editingType, setEditingType] = useState<TaskTypeWithDefault | null>(null);
  const [editName, setEditName] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteType, setDeleteType] = useState<TaskTypeWithDefault | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const EMPTY_CLIENT_FORM = {
    clientName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    state: '',
    country: '',
    pincode: '',
    gstin: '',
    pan: '',
    branchDetails: [] as Array<{
      branchName: string;
      gstin: string;
      address1: string;
      address2: string;
      state: string;
      country: string;
      pincode: string;
    }>,
  };
  const [newClientForm, setNewClientForm] = useState(EMPTY_CLIENT_FORM);
  const [savingClient, setSavingClient] = useState(false);
  const [gstinLookingUpClient, setGstinLookingUpClient] = useState(false);
  const [gstinLookupClientMsg, setGstinLookupClientMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // Per-branch GST lookup state (only one branch fetches at a time).
  const [clientBranchLookupIdx, setClientBranchLookupIdx] = useState<number | null>(null);
  const [clientBranchLookupMsg, setClientBranchLookupMsg] = useState<{
    idx: number;
    kind: 'ok' | 'err';
    text: string;
  } | null>(null);
  // Centralised close-and-reset so every dismissal path (Cancel, backdrop,
  // explicit X) ends with the form blank for the next open.
  const closeClientModal = () => {
    setShowAddClientModal(false);
    setNewClientForm(EMPTY_CLIENT_FORM);
    setGstinLookupClientMsg(null);
    setClientBranchLookupIdx(null);
    setClientBranchLookupMsg(null);
  };
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [showAddVendorModal, setShowAddVendorModal] = useState(false);
  const [newVendorForm, setNewVendorForm] = useState({ clientName: '', email: '', phone: '', address1: '', address2: '', state: '', country: '', pincode: '', gstin: '', pan: '' });
  const [savingVendor, setSavingVendor] = useState(false);
  const [gstinLookingUpVendor, setGstinLookingUpVendor] = useState(false);
  const [gstinLookupVendorMsg, setGstinLookupVendorMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [newProjectForm, setNewProjectForm] = useState({ name: '', description: '', client_id: '' });
  const [savingProject, setSavingProject] = useState(false);
  const [dueOpen, setDueOpen] = useState(false);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [dueTime, setDueTime] = useState<string>("");
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [selectedPreparors, setSelectedPreparors] = useState<string[]>([]);
  const [extraGroupMemberIds, setExtraGroupMemberIds] = useState<string[]>([]);
  const [formData, setFormData] = useState<TaskCreatePayload & { approvalChain?: ApprovalLevel[]; recurrence_interval?: RecurrenceInterval; recurrence_every?: number }>(
    buildInitialForm(chatContext, creatorId)
  );

  // Effective mode: in addition to the explicit `mode` prop, ANY time a
  // cross-org linked vendor is selected we transparently flip into
  // 'cross-org-draft' behavior — Approval tab hides, submit routes to
  // /tasks/cross-org-draft, target_head_office_id is read off the vendor.
  // This makes the standard "create task and pick a vendor" flow work
  // without the user having to enter through the Vendors panel.
  const selectedVendor = (formData as any).vendor_id
    ? vendors.find((v) => v.id === (formData as any).vendor_id)
    : null;
  const vendorLinkedHo = selectedVendor?.linked_headOfficeID || null;
  const effectiveMode: TaskCreateMode =
    mode === 'admin-review'
      ? 'admin-review'
      : mode === 'cross-org-draft' || !!vendorLinkedHo
      ? 'cross-org-draft'
      : 'standard';
  const effectiveTargetHo = targetHeadOfficeId || vendorLinkedHo || undefined;

  // Messenger divergence: hide the Approval Chain tab whenever ANY vendor
  // is selected (not only linked ones). Submit behavior still follows
  // effectiveMode — unlinked vendor stays 'standard'.
  const hideApprovalTab = effectiveMode === 'cross-org-draft' || !!selectedVendor;

  // When the approval tab is hidden, make sure the active section can never
  // land on it (e.g. if upstream code mutates activeSection programmatically,
  // or the user selects a vendor while standing on the Approval tab). Fall
  // back to 'details'.
  useEffect(() => {
    if (hideApprovalTab && activeSection === 'approval') {
      setActiveSection('details');
    }
  }, [hideApprovalTab, activeSection]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await getAllUsers();
      const usersData = response?.users || [];
      setUsers(usersData);
      console.log('Users fetched:', usersData.length);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
      toast({
        description: "Failed to load users",
        variant: "destructive",
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchTaskTypes();
      fetchUsers();
      clientsApi.list({ page_size: 200 }).then(r => setClients(r.data?.items || [])).catch(() => { });
      vendorsApi.list({ page_size: 200 }).then(r => setVendors(r.data?.items || [])).catch(() => { });
      projectsApi.list({ page_size: 200 }).then(r => setProjects(r.data?.items || [])).catch(() => { });
      // Pull the next task code from the branch's series settings. Shown
      // read-only on the Basic section so the user knows what id the new
      // task will get. Silently no-ops if the series isn't configured yet.
      tasksApi.getCodeSettings()
        .then(r => setNextTaskCode(r.data?.settings?.next_preview || null))
        .catch(() => setNextTaskCode(null));
    }
  }, [isOpen]);

  const fetchTaskTypes = async () => {
    setLoadingTaskTypes(true);
    try {
      const response = await tasksApi.getTaskTypes();
      const taskTypesData = Array.isArray(response.data?.items) ? response.data.items : [];
      setTaskTypes(taskTypesData);
    } catch (error) {
      console.error('Failed to fetch task types:', error);
      setTaskTypes([]);
      toast({
        description: "Failed to load task types",
        variant: "destructive",
      });
    } finally {
      setLoadingTaskTypes(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setActiveSection('basic');
    }
  }, [chatContext, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setFormData({
      ...buildInitialForm(chatContext, creatorId),
      ...(defaultProjectId ? { project_id: defaultProjectId } : {}),
      ...(defaultClientId ? { client_id: defaultClientId } : {}),
    } as any);
    setSelectedPreparors([]);
    setActiveSection('basic');
    setDueDate(null);
    setDueTime("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, defaultProjectId, defaultClientId, creatorId]);

  // Admin-review pre-fill: load the draft so the admin sees the originator's
  // submitted data before deciding to Approve / Send Back / Reject.
  useEffect(() => {
    if (mode !== 'admin-review' || !draftTaskId || !isOpen) return;
    (async () => {
      try {
        const res = await tasksApi.get(draftTaskId);
        // Endpoint may return either the raw task or { task, assignees, ... }.
        const t: any = res.data?.task || res.data || {};
        setFormData(prev => ({
          ...prev,
          title: t.title || prev.title,
          description: t.description || prev.description,
          priority: t.priority || prev.priority,
          due_date: t.due_date || prev.due_date,
          task_type: t.task_type || prev.task_type,
          client_id: t.client_id ?? (prev as any).client_id,
          vendor_id: t.vendor_id ?? (prev as any).vendor_id,
          approvalChain: (t.approval_chain && Array.isArray(t.approval_chain) && t.approval_chain.length > 0)
            ? t.approval_chain
            : prev.approvalChain,
        } as any));
      } catch (err: any) {
        toast({
          title: 'Could not load draft',
          description: err?.message || String(err),
          variant: 'destructive',
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, draftTaskId, isOpen]);

  // Reason modal state for Send Back / Reject actions in admin-review mode.
  const [reasonModal, setReasonModal] = useState<null | 'send-back' | 'reject'>(null);
  const [reasonText, setReasonText] = useState('');

  const handleApprove = async () => {
    if (!draftTaskId) return;
    try {
      await tasksApi.promoteDraft(draftTaskId, {
        assignees: (selectedPreparors || [])
          .filter(p => !!p)
          .map(p => ({ user_id: p, role_in_task: 'PREPAROR' })),
        approval_chain: (formData.approvalChain || [])
          .filter((l: any) => !!l.approver)
          .map((l: any) => ({
            level: l.level,
            approver: l.approver,
            role_label: l.roleLabel,
          })),
        priority: formData.priority,
        due_date: formData.due_date || null,
      });
      toast({ title: 'Draft approved', description: 'Task is now active.' });
      onSuccess?.();
      onCreated?.(draftTaskId);
      onClose?.();
    } catch (err: any) {
      toast({
        title: 'Approve failed',
        description: err?.response?.data?.detail || err?.message || 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const submitReason = async () => {
    if (!draftTaskId || !reasonModal || !reasonText.trim()) return;
    try {
      if (reasonModal === 'send-back') {
        await tasksApi.sendBackDraft(draftTaskId, reasonText);
        toast({ title: 'Draft sent back', description: 'Originator has been notified.' });
      } else {
        await tasksApi.rejectDraft(draftTaskId, reasonText);
        toast({ title: 'Draft rejected', description: 'Originator has been notified.' });
      }
      setReasonModal(null);
      setReasonText('');
      onSuccess?.();
      onCreated?.(draftTaskId);
      onClose?.();
    } catch (err: any) {
      toast({
        title: 'Action failed',
        description: err?.response?.data?.detail || err?.message || 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData(buildInitialForm(chatContext, creatorId));
    setSelectedPreparors([]);
    setExtraGroupMemberIds([]);
    setDueDate(null);
    setDueTime("");
    setStagedAttachments([]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleTaskTypeChange = (value: string) => {
    if (value === '__CREATE_NEW__') {
      setShowCreateTaskTypeModal(true);
    } else {
      setFormData({ ...formData, task_type: value });
    }
  };

  const handleEditType = (type: TaskTypeWithDefault) => {
    setEditingType(type);
    setEditName(type.name);
    setShowEditModal(true);
  };

  const handleDeleteType = (type: TaskTypeWithDefault) => {
    setDeleteType(type);
    setShowDeleteModal(true);
  };

  const handleUpdateTaskType = async () => {
    if (!editingType || !editName.trim()) {
      toast({
        description: "Please enter a task type name",
        variant: "destructive",
      });
      return;
    }

    setCreatingTaskType(true);
    try {
      await tasksApi.updateTaskType(editingType.id, { name: editName.trim() });
      await fetchTaskTypes();
      setShowEditModal(false);
      setEditingType(null);
      setEditName("");
      toast({
        description: "Task type updated successfully",
        variant: "default",
      });
    } catch (error: any) {
      console.error('Failed to update task type:', error);
      const message = error?.response?.data?.message || error?.response?.data?.detail || error?.response?.data?.error || "Failed to update task type";
      toast({
        description: message,
        variant: "destructive",
      });
    } finally {
      setCreatingTaskType(false);
    }
  };

  const handleDeleteTaskType = async () => {
    if (!deleteType) return;

    setLoading(true);
    try {
      await tasksApi.deleteTaskType(deleteType.id);
      await fetchTaskTypes();

      if (formData.task_type === deleteType.name) {
        setFormData({ ...formData, task_type: '' });
      }

      setShowDeleteModal(false);
      setDeleteType(null);
      toast({
        description: "Task type deleted successfully",
        variant: "default",
      });
    } catch (error: any) {
      console.error('Failed to delete task type:', error);
      const message = error?.response?.data?.message || error?.response?.data?.detail || error?.response?.data?.error || "Failed to delete task type";
      toast({
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTaskType = async () => {
    if (!newTaskTypeName.trim()) {
      toast({
        description: "Please enter a task type name",
        variant: "destructive",
      });
      return;
    }

    setCreatingTaskType(true);
    try {
      const response = await tasksApi.createTaskType({ name: newTaskTypeName });
      const newTaskType = response.data;

      await fetchTaskTypes();
      setFormData({ ...formData, task_type: newTaskType.name });
      setShowCreateTaskTypeModal(false);
      setNewTaskTypeName('');
      toast({
        description: "Task type created successfully",
        variant: "default",
      });
    } catch (error: any) {
      console.error('Failed to create task type:', error);
      const message = error?.response?.data?.message || error?.response?.data?.detail || error?.response?.data?.error || "This task type already exists";
      toast({
        description: message,
        variant: "destructive",
      });
    } finally {
      setCreatingTaskType(false);
    }
  };

  const addPreparor = () => {
    setSelectedPreparors([...selectedPreparors, '']);
  };

  const removePreparor = (index: number) => {
    setSelectedPreparors(selectedPreparors.filter((_, i) => i !== index));
  };

  const updatePreparor = (index: number, userId: string) => {
    const newPreparors = [...selectedPreparors];
    newPreparors[index] = userId;
    setSelectedPreparors(newPreparors);
  };

  const renumberApprovalChain = (chain: ApprovalLevel[]): ApprovalLevel[] =>
    chain.map((item, i) => {
      const newLevel = i + 1;
      const isDefaultLabel = /^Level \d+ Approver$/.test(item.roleLabel || '');
      return {
        ...item,
        level: newLevel,
        roleLabel: isDefaultLabel ? `Level ${newLevel} Approver` : item.roleLabel,
      };
    });

  const addApprovalLevel = () => {
    setFormData((prev) => {
      const nextLevel = (prev.approvalChain?.length || 0) + 1;
      return {
        ...prev,
        approvalChain: renumberApprovalChain([
          ...(prev.approvalChain || []),
          {
            level: nextLevel,
            approver: '',
            roleLabel: `Level ${nextLevel} Approver`,
            status: 'Pending',
            actionAt: null,
            remark: '',
          },
        ]),
      };
    });
  };

  const removeApprovalLevel = (index: number) => {
    if ((formData.approvalChain?.length || 0) <= 1) return;
    setFormData((prev) => ({
      ...prev,
      approvalChain: renumberApprovalChain(
        (prev.approvalChain || []).filter((_, i) => i !== index)
      ),
    }));
  };

  const updateApprovalLevel = (index: number, field: keyof ApprovalLevel, value: string) => {
    setFormData((prev) => ({
      ...prev,
      approvalChain: (prev.approvalChain || []).map((level, i) =>
        i === index ? { ...level, [field]: value } : level
      ),
    }));
  };

  const handleChecklistChange = useCallback((index: number, value: string) => {
    setFormData(prev => {
      const newChecklist = [...(prev.checklist || [])];
      newChecklist[index] = value;
      return { ...prev, checklist: newChecklist };
    });
  }, []);

  const addChecklistItem = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      checklist: [...(prev.checklist || []), ''],
    }));
  }, []);

  const removeChecklistItem = useCallback((index: number) => {
    setFormData(prev => {
      const newChecklist = (prev.checklist || []).filter((_, i) => i !== index);
      if (newChecklist.length === 0) {
        return { ...prev, checklist: [''] };
      }
      return { ...prev, checklist: newChecklist };
    });
  }, []);


  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;

    const updated = new Date(date);

    if (dueTime) {
      const [h, m] = dueTime.split(":");
      updated.setHours(Number(h));
      updated.setMinutes(Number(m));
    } else {
      updated.setHours(18);
      updated.setMinutes(0);
    }

    setDueDate(updated);

    setFormData((prev) => ({
      ...prev,
      due_date: formatLocalDateTime(updated),
    }));

    setDueOpen(false);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = e.target.value;
    setDueTime(time);

    if (dueDate) {
      const [hours, minutes] = time.split(':');
      const updated = new Date(dueDate);
      updated.setHours(parseInt(hours), parseInt(minutes));
      setDueDate(updated);
      setFormData((prev) => ({
        ...prev,
        due_date: formatLocalDateTime(updated),
      }));
    }
  };

  const handleTimeBlur = () => {
    setTimeout(() => {
      setDueOpen(false);
    }, 200);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Admin-review submission is wired separately (Task 22) via dedicated
    // promote / send-back / reject action buttons, not via the form submit.
    if (mode === 'admin-review') {
      return;
    }

    const isCrossOrgDraft = effectiveMode === 'cross-org-draft';

    // Cross-org-draft mode does NOT require the originator to choose a
    // preparor — the receiving head office admin assigns workers when they
    // promote the draft. Skip the preparor check in that mode.
    if (!isCrossOrgDraft) {
      if (selectedPreparors.length === 0 || selectedPreparors.every(p => !p)) {
        toast({
          description: "Please select at least one Preparor (Worker)",
          variant: "destructive",
        });
        return;
      }
    }

    if (isCrossOrgDraft && !effectiveTargetHo) {
      toast({
        description:
          "Cross-org task requires a linked vendor. Selected vendor has no linked head office.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    const assignees: Array<{ user_id: string; role_in_task: 'PREPAROR' | 'REVIEWER' }> = [];

    selectedPreparors.forEach(preparorId => {
      if (preparorId) {
        assignees.push({
          user_id: preparorId,
          role_in_task: 'PREPAROR' as const,
        });
      }
    });

    (formData.approvalChain || []).forEach(level => {
      if (level.approver) {
        assignees.push({
          user_id: level.approver,
          role_in_task: 'REVIEWER' as const,
        });
      }
    });

    const recurrence = buildRecurrence(formData);
    const payload: TaskCreatePayload = {
      title: formData.title,
      description: formData.description || undefined,
      task_type: formData.task_type,
      priority: formData.priority,
      department_id: formData.department_id || undefined,
      // start_date == the "Task Date" picker. Sent to the backend as the
      // due_date of every Todo it auto-creates from the checklist.
      start_date: formData.start_date || undefined,
      due_date: formData.due_date || undefined,
      estimated_hours: formData.estimated_hours ? parseFloat(String(formData.estimated_hours)) : undefined,
      checklist: (formData.checklist || []).filter(item => item.trim()),
      assignees: assignees,
      // In cross-org-draft mode the server resolves client_id automatically
      // from the cross-org link; the picker is hidden in the UI as well.
      client_id: isCrossOrgDraft ? null : ((formData as any).client_id || null),
      vendor_id: (formData as any).vendor_id || null,
      project_id: (formData as any).project_id || null,
      extra_group_member_ids: extraGroupMemberIds.filter(Boolean),
      is_recurring: recurrence.is_recurring,
      recurrence_rule: recurrence.recurrence_rule,
    };

    try {
      const response = isCrossOrgDraft
        ? await tasksApi.createCrossOrgDraft({
            ...payload,
            target_head_office_id: effectiveTargetHo as string,
            is_cross_org_draft: true,
          })
        : await tasksApi.create(payload);
      const newTaskId = response.data.task.id;

      // Upload any staged attachments now that we have a real task id.
      // Failures are reported per-file via a toast but don't block the
      // overall task-creation success — the task itself was saved.
      if (stagedAttachments.length > 0) {
        setUploadingAttachments(true);
        const failed: string[] = [];
        for (const file of stagedAttachments) {
          try {
            await tasksApi.uploadAttachment(newTaskId, file);
          } catch (err: any) {
            failed.push(`${file.name} (${err?.response?.data?.detail || err?.message || 'upload failed'})`);
          }
        }
        setUploadingAttachments(false);
        if (failed.length > 0) {
          toast({
            title: 'Some attachments failed',
            description: failed.join(', '),
            variant: 'destructive',
          });
        }
      }

      resetForm();
      onCreated?.(newTaskId);
      if (onClose) onClose();
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/tasks/${newTaskId}`);
      }
    } catch (err: any) {
      toast({
        description: err?.response?.data?.detail || err.message || 'Failed to create task',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setUploadingAttachments(false);
    }
  };

  const handleClose = () => {
    resetForm();
    if (onClose) {
      onClose();
      return;
    }
    navigate('/tasks/list');
  };

  const isBasicValid = formData.title.trim().length > 0;

  const calculateProgress = () => {
    const totalFields = 9;
    let filledFields = 0;

    if (formData.title) filledFields++;
    if (formData.description) filledFields++;
    if (formData.task_type) filledFields++;
    if (formData.priority) filledFields++;
    if (formData.department_id) filledFields++;
    if (formData.due_date) filledFields++;
    if (formData.estimated_hours) filledFields++;
    if (selectedPreparors.length > 0 && selectedPreparors.some(p => p)) filledFields++;
    if (formData.checklist && formData.checklist.length > 0 && formData.checklist.some(item => item.trim())) filledFields++;

    return (filledFields / totalFields) * 100;
  };

  const progress = calculateProgress();

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId || u.user_id === userId);
    if (user) {
      const dept = user.department || user.department_id || '';
      return `${user.name}${dept ? ` (${dept})` : ''}`;
    }
    return '-- Select --';
  };

  const visibleUsers = chatContext?.availableUserIds?.length
    ? users.filter(user => chatContext.availableUserIds?.includes(user.id || user.user_id || ''))
    : users;

  const preparorUsers = visibleUsers;
  const approverUsers = visibleUsers;

  const formatLocalDateTime = (date: Date) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const timeRef = React.useRef<HTMLInputElement>(null);
  const checklistRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!checklistRef.current) return;
    checklistRef.current.scrollTop = checklistRef.current.scrollHeight;
  }, [formData.checklist?.length]);

  const handleSaveNewClient = async () => {
    if (!newClientForm.clientName.trim()) return;
    setSavingClient(true);
    try {
      const payload: any = {
        clientName: newClientForm.clientName.trim(),
        email: newClientForm.email || undefined,
        phone: newClientForm.phone || undefined,
        address1: newClientForm.address1 || undefined,
        address2: newClientForm.address2 || undefined,
        state: newClientForm.state || undefined,
        country: newClientForm.country || undefined,
        pincode: newClientForm.pincode || undefined,
        gstin: newClientForm.gstin || undefined,
        pan: newClientForm.pan || undefined,
      };
      if (newClientForm.branchDetails.length > 0) {
        payload.branchDetails = newClientForm.branchDetails;
      }
      const r = await clientsApi.create(payload);
      const created = r.data?.client;
      if (created) {
        setClients(prev => [...prev, created]);
        setFormData(prev => ({ ...prev, client_id: created.id } as any));
      }
      // Same reset path the cancel button uses.
      closeClientModal();
      toast({
        description: "Client added successfully",
        variant: "default",
      });
    } catch (error) {
      toast({
        description: "Failed to add client",
        variant: "destructive",
      });
    } finally {
      setSavingClient(false);
    }
  };
  const addClientBranch = () => {
    setNewClientForm(f => ({
      ...f,
      branchDetails: [
        ...f.branchDetails,
        { branchName: '', gstin: '', address1: '', address2: '', state: '', country: '', pincode: '' },
      ],
    }));
  };
  const removeClientBranch = (idx: number) => {
    setNewClientForm(f => ({
      ...f,
      branchDetails: f.branchDetails.filter((_, i) => i !== idx),
    }));
  };
  const updateClientBranch = (
    idx: number,
    key: keyof (typeof EMPTY_CLIENT_FORM)['branchDetails'][number],
    value: string,
  ) => {
    setNewClientForm(f => ({
      ...f,
      branchDetails: f.branchDetails.map((b, i) =>
        i === idx ? { ...b, [key]: value } : b,
      ),
    }));
  };
  const lookupGstinForBranch = async (idx: number, gstin: string) => {
    const value = (gstin || '').trim().toUpperCase();
    if (value.length !== 15) {
      setClientBranchLookupMsg({ idx, kind: 'err', text: 'GSTIN must be 15 characters.' });
      return;
    }
    setClientBranchLookupIdx(idx);
    setClientBranchLookupMsg(null);
    try {
      const res = await fetch(`https://api.startupkhata.com/validateGstNo?gst=${value}`);
      if (!res.ok) throw new Error(`Lookup failed (${res.status})`);
      const data = await res.json();
      const addr =
        data?.response?.principalPlaceOfBusinessFields?.principalPlaceOfBusinessAddress
        || {};
      const address1 = `${addr.buildingNumber || ''} ${addr.buildingName || ''} ${addr.streetName || ''}`.trim();
      const address2 = `${addr.location || ''} ${addr.locality || ''} ${addr.districtName || ''}`.trim();
      setNewClientForm(prev => ({
        ...prev,
        branchDetails: prev.branchDetails.map((b, i) =>
          i === idx
            ? {
                ...b,
                gstin: value,
                address1: address1 || b.address1,
                address2: address2 || b.address2,
                state: addr.stateName || b.state,
                country: addr.country || b.country || 'India',
                pincode: addr.pincode || b.pincode,
              }
            : b,
        ),
      }));
      setClientBranchLookupMsg({ idx, kind: 'ok', text: 'Branch details auto-filled.' });
    } catch {
      setClientBranchLookupMsg({
        idx,
        kind: 'err',
        text: 'GST fetch failed. Check the GSTIN and try again.',
      });
    } finally {
      setClientBranchLookupIdx(null);
    }
  };

  const lookupGstinFor = async (
    surface: 'client' | 'vendor',
    gstin: string,
  ) => {
    const setLoading = surface === 'client' ? setGstinLookingUpClient : setGstinLookingUpVendor;
    const setMsg = surface === 'client' ? setGstinLookupClientMsg : setGstinLookupVendorMsg;
    const setForm = surface === 'client' ? setNewClientForm : setNewVendorForm;
    const value = (gstin || '').trim().toUpperCase();
    if (value.length !== 15) {
      setMsg({ kind: 'err', text: 'GSTIN must be 15 characters.' });
      return;
    }
    setLoading(true);
    setMsg(null);
    try {
      const res = await fetch(`https://api.startupkhata.com/validateGstNo?gst=${value}`);
      if (!res.ok) throw new Error(`Lookup failed (${res.status})`);
      const data = await res.json();
      const addr =
        data?.response?.principalPlaceOfBusinessFields?.principalPlaceOfBusinessAddress
        || {};
      const address1 = `${addr.buildingNumber || ''} ${addr.buildingName || ''} ${addr.streetName || ''}`.trim();
      const address2 = `${addr.location || ''} ${addr.locality || ''} ${addr.districtName || ''}`.trim();
      setForm(prev => ({
        ...prev,
        gstin: value,
        pan: value.slice(2, 12),  // chars 3-12 of a GSTIN are the PAN
        clientName: data?.name || prev.clientName,
        address1: address1 || prev.address1,
        address2: address2 || prev.address2,
        state: addr.stateName || prev.state,
        country: addr.country || prev.country || 'India',
        pincode: addr.pincode || prev.pincode,
      }));
      setMsg({ kind: 'ok', text: 'GSTIN details auto-filled successfully.' });
    } catch {
      setMsg({ kind: 'err', text: 'GST fetch failed. Please check the GSTIN and try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNewVendor = async () => {
    if (!newVendorForm.clientName.trim()) return;
    setSavingVendor(true);
    try {
      const r = await vendorsApi.create({
        clientName: newVendorForm.clientName.trim(),
        email: newVendorForm.email || undefined,
        phone: newVendorForm.phone || undefined,
        address1: newVendorForm.address1 || undefined,
        address2: newVendorForm.address2 || undefined,
        state: newVendorForm.state || undefined,
        country: newVendorForm.country || undefined,
        pincode: newVendorForm.pincode || undefined,
        gstin: newVendorForm.gstin || undefined,
        pan: newVendorForm.pan || undefined,
      });
      const created = r.data?.client || r.data?.vendor;
      if (created) {
        setVendors(prev => [...prev, created]);
        setFormData(prev => ({ ...prev, vendor_id: created.id } as any));
      }
      setNewVendorForm({ clientName: '', email: '', phone: '', address1: '', address2: '', state: '', country: '', pincode: '', gstin: '', pan: '' });
      setGstinLookupVendorMsg(null);
      setShowAddVendorModal(false);
      toast({ description: 'Vendor added successfully', variant: 'default' });
    } catch (error) {
      toast({ description: 'Failed to add vendor', variant: 'destructive' });
    } finally {
      setSavingVendor(false);
    }
  };

  const handleSaveNewProject = async () => {
    if (!newProjectForm.name.trim()) return;
    setSavingProject(true);
    try {
      const r = await projectsApi.create({
        name: newProjectForm.name.trim(),
        description: newProjectForm.description || undefined,
        client_id: newProjectForm.client_id || null,
      });
      const created = r.data?.project;
      if (created) {
        setProjects(prev => [...prev, created]);
        setFormData(prev => ({
          ...prev,
          project_id: created.id,
          ...(created.client_id ? { client_id: created.client_id } : {}),
        } as any));
      }
      setNewProjectForm({ name: '', description: '', client_id: '' });
      setShowAddProjectModal(false);
      toast({ description: 'Project added successfully', variant: 'default' });
    } catch (error) {
      toast({ description: 'Failed to add project', variant: 'destructive' });
    } finally {
      setSavingProject(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="gt-themed">
      {/* Backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-300"
        style={{ background: 'rgba(15, 29, 20, 0.32)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', zIndex: 100 }}
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 overflow-y-auto" style={{ zIndex: 101 }}>
        <div className="flex min-h-full items-center justify-center p-4">
          <div
            className="relative w-full max-w-4xl max-h-[94vh] overflow-hidden gt-modal flex flex-col animate-in fade-in zoom-in duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header — more compact on mobile so the form body gets more room */}
            <div className="shrink-0 px-4 md:px-6 py-3 md:py-5"
              style={{
                background: 'linear-gradient(135deg, var(--gt-primary-container) 0%, var(--gt-surface-1) 100%)',
                borderBottom: '1px solid var(--gt-divider)',
              }}>
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="gt-leaf" style={{ width: 10, height: 10 }} />
                  </div>
                  <h2 className="gt-display" style={{ color: 'var(--gt-text-primary)' }}>
                    <span className="text-[20px] md:text-[26px]">
                      Create <em className="gt-serif-italic" style={{ color: 'var(--gt-accent-strong)' }}>New Task</em>
                    </span>
                  </h2>
                  <p className="gt-page-sub hidden md:block" style={{ marginTop: 4 }}>Fill in the details to <em className="gt-serif-italic">begin</em>.</p>
                </div>

                {/* Form Completion — sits to the right of the title */}
                <div className="hidden sm:flex flex-col items-end shrink-0 w-[180px] md:w-[220px] mt-1">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="gt-eyebrow" style={{ marginBottom: 0 }}>Form Completion</span>
                    <span className="gt-display gt-numerals" style={{ color: 'var(--gt-accent-strong)', fontSize: 18 }}>{Math.round(progress)}%</span>
                  </div>
                  <div className="gt-progress-track w-full">
                    <div
                      className="gt-progress-fill"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                <button onClick={handleClose} className="gt-icon-btn shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Progress Bar — mobile only (on sm+ it's inline next to the title above) */}
              <div className="mt-3 sm:hidden">
                <div className="flex justify-between gt-page-sub mb-1.5 gt-numerals" style={{ fontSize: 11 }}>
                  <span className="gt-eyebrow" style={{ marginBottom: 0 }}>Form Completion</span>
                  <span className="gt-display" style={{ color: 'var(--gt-accent-strong)' }}>{Math.round(progress)}%</span>
                </div>
                <div className="gt-progress-track">
                  <div
                    className="gt-progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Step Indicator — single-line horizontal scroll on mobile (was
                wrapping to 2 rows and squeezing the form body). */}
            <div className="shrink-0 px-4 md:px-6 pt-2 md:pt-3" style={{ borderBottom: '1px solid var(--gt-border)' }}>
              <nav className="flex gap-1 overflow-x-auto flex-nowrap gt-task-tabs">
                {(['basic', 'details', 'approval', 'checklist', 'attachments'] as const)
                  .filter((s) => !(hideApprovalTab && s === 'approval'))
                  .map((section) => {
                  const isActive = activeSection === section;
                  return (
                    <button
                      key={section}
                      type="button"
                      onClick={() => goToSection(section as any)}
                      onMouseEnter={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.color = 'var(--gt-primary-deep)';
                          e.currentTarget.style.background = 'var(--gt-primary-container)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) {
                          e.currentTarget.style.color = 'var(--gt-text-tertiary)';
                          e.currentTarget.style.background = 'transparent';
                        }
                      }}
                      className="transition-all"
                      style={{
                        padding: '10px 18px',
                        fontFamily: 'var(--gt-font-display)',
                        fontSize: 15,
                        fontWeight: 600,
                        letterSpacing: '0.01em',
                        borderTopLeftRadius: 10,
                        borderTopRightRadius: 10,
                        border: 'none',
                        borderBottom: isActive ? '2px solid var(--gt-primary)' : '2px solid transparent',
                        background: isActive ? 'var(--gt-surface-0)' : 'transparent',
                        color: isActive ? 'var(--gt-primary-deep)' : 'var(--gt-text-tertiary)',
                        boxShadow: isActive ? 'var(--gt-elev-1)' : 'none',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        marginBottom: -1,
                      }}
                    >
                      {/* "Approval Chain" is too wide for phones — show
                          shortened "Approval" on mobile, full label on md+ */}
                      {section === 'approval' ? (
                        <>
                          <span className="md:hidden">Approval</span>
                          <span className="hidden md:inline">Approval Chain</span>
                        </>
                      ) : (
                        section.charAt(0).toUpperCase() + section.slice(1)
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Form Body — flex-1 so it fills the remaining modal height and
                each section's action buttons stay visible at the bottom. */}
            <div ref={formBodyRef} className="flex-1 min-h-0 overflow-y-auto p-3 md:p-5 pb-[env(safe-area-inset-bottom,16px)]">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Basic Information Section */}
                {activeSection === 'basic' && (
                  <div className="space-y-3 animate-in fade-in duration-300">
                    {/* Row 1: Task ID (read-only preview) and Task Title side
                        by side so the form fits without scrolling. Task ID
                        is rendered only when the per-branch series is
                        configured (nextTaskCode non-null). When it isn't
                        configured, Task Title spans the full width. */}
                    <div className={`grid grid-cols-1 gap-4 ${nextTaskCode ? 'md:grid-cols-3' : ''}`}>
                      {nextTaskCode && (
                        <div className="md:col-span-1">
                          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                            Task ID
                            <span className="ml-1 text-xs font-normal text-gray-400">
                              (auto-generated)
                            </span>
                          </label>
                          <input
                            type="text"
                            value={nextTaskCode}
                            readOnly
                            disabled
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-700 cursor-not-allowed font-mono tracking-wide"
                            title="The next id in the series. Generated when the task is created."
                          />
                        </div>
                      )}

                      <div className={nextTaskCode ? 'md:col-span-2' : ''}>
                        <div className="flex justify-between items-end mb-1.5 gap-3">
                          <label className="block text-sm font-semibold text-gray-700">
                            Task Title <span className="text-red-500">*</span>
                          </label>
                          <p className="text-xs text-gray-400">
                            {formData.title.length}/500
                          </p>
                        </div>
                        <input
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleChange}
                          required
                          maxLength={500}
                          placeholder="e.g., Complete project documentation"
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                          autoFocus
                        />
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-end mb-1.5 gap-3">
                        <label className="block text-sm font-semibold text-gray-700">Description</label>
                        <ErrorBoundary inline name="ai-generate-description">
                          <AiGenerateButton
                            idleLabel={formData.description ? 'Regenerate with AI' : 'Generate with AI'}
                            disabled={!formData.title.trim()}
                            disabledHint="Enter a title first"
                            run={async () => {
                              const r = await generateTaskDescription(formData.title, formData.task_type);
                              if (!r.ok) return r.message || 'AI is unavailable right now.';
                              setFormData(prev => ({ ...prev, description: r.description || '' }));
                              return null;
                            }}
                          />
                        </ErrorBoundary>
                      </div>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={3}
                        placeholder="Describe what needs to be done..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                      />
                    </div>

                    <div className="flex justify-end pt-2">
                      <button
                        type="button"
                        onClick={() => goToSection('details')}
                        disabled={!isBasicValid}
                        className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-1.5 shadow-md"
                      >
                        Next: Details
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Details Section */}
                {activeSection === 'details' && (
                  <div className="space-y-3 animate-in fade-in duration-300">
                    {/* Row 1: Task Type | Priority | Repeat this task */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Task Type</label>
                        <div className="relative">
                          <div
                            onClick={() => setShowTypeDropdown(prev => !prev)}
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg cursor-pointer flex justify-between items-center bg-white"
                          >
                            <span className={formData.task_type ? "text-gray-900" : "text-gray-400"}>
                              {formData.task_type || "Select Type"}
                            </span>
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>

                          {showTypeDropdown && (
                            <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto py-1" style={{ zIndex: 200 }}>
                              {taskTypes.map((type) => {
                                const isSelected = formData.task_type === type.name;
                                return (
                                  <div
                                    key={type.id}
                                    className={`flex items-center justify-between px-3 py-1.5 cursor-pointer group transition-colors ${isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-700'}`}
                                    onClick={() => {
                                      setFormData({ ...formData, task_type: type.name });
                                      setShowTypeDropdown(false);
                                    }}
                                  >
                                    <span className="flex-1 text-sm">{type.name}</span>
                                    {!type.is_default && (
                                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditType(type);
                                          }}
                                          className="text-blue-500 hover:text-blue-700 text-xs px-1 rounded"
                                          title="Edit"
                                        >
                                          ✏️
                                        </button>
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteType(type);
                                          }}
                                          className="text-red-500 hover:text-red-700 text-xs px-1 rounded"
                                          title="Delete"
                                        >
                                          🗑️
                                        </button>
                                      </div>
                                    )}
                                    {type.is_default && (
                                      <span className="text-[10px] uppercase tracking-wide text-gray-400 ml-2">Default</span>
                                    )}
                                  </div>
                                );
                              })}

                              <div
                                onClick={() => {
                                  setShowCreateTaskTypeModal(true);
                                  setShowTypeDropdown(false);
                                }}
                                className="px-3 py-1.5 mt-1 text-sm text-blue-600 font-medium hover:bg-blue-50 cursor-pointer border-t border-gray-100 transition-colors"
                              >
                                + Create New Type
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Priority</label>
                        <Select
                          value={formData.priority}
                          onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v as any }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Priority" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LOW">🟢 Low</SelectItem>
                            <SelectItem value="MEDIUM">🟡 Medium</SelectItem>
                            <SelectItem value="HIGH">🟠 High</SelectItem>
                            <SelectItem value="URGENT">🔴 Urgent</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Repeat this task — compact toggle in row 1; expanded
                          schedule options render full-width below the row. */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Repeat this task</label>
                        <label className="w-full px-4 py-2.5 border border-gray-300 rounded-lg cursor-pointer flex items-center gap-2 bg-white">
                          <input
                            type="checkbox"
                            checked={!!formData.is_recurring}
                            onChange={(e) =>
                              setFormData((prev) => ({ ...prev, is_recurring: e.target.checked }))
                            }
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <span className="text-sm text-gray-700">
                            {formData.is_recurring ? 'Recurring' : 'One-time task'}
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Recurrence options — only when "Repeat this task" is on */}
                    {formData.is_recurring && (
                      <div className="rounded-lg border border-gray-200 bg-gray-50/70 px-3.5 py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm text-gray-600">Every</span>
                          <input
                            type="number"
                            min={1}
                            value={formData.recurrence_every ?? 1}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                recurrence_every: Math.max(1, parseInt(e.target.value || '1', 10)),
                              }))
                            }
                            className="w-16 px-2 py-1 text-sm border border-gray-300 rounded bg-white"
                          />
                          <div className="w-36">
                            <Select
                              value={formData.recurrence_interval || 'WEEKLY'}
                              onValueChange={(v) =>
                                setFormData((prev) => ({ ...prev, recurrence_interval: v as RecurrenceInterval }))
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="DAILY">Daily</SelectItem>
                                <SelectItem value="WEEKLY">Weekly</SelectItem>
                                <SelectItem value="MONTHLY">Monthly</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <span className="w-full text-xs text-gray-500">
                            A copy will be created every {formData.recurrence_every ?? 1}{' '}
                            {intervalUnit(formData.recurrence_interval || 'WEEKLY', formData.recurrence_every ?? 1)}, starting{' '}
                            {format(
                              new Date(
                                computeNextSpawnAt(
                                  formData.recurrence_interval || 'WEEKLY',
                                  formData.recurrence_every ?? 1,
                                ),
                              ),
                              'MMM dd, yyyy',
                            )}
                            .
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Row 2: Task Date | Due Date | Estimated Hours */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                      {/* Task Date — the date the work is planned for. Every
                          checklist item below is auto-created as a Todo on
                          this date in the assignee's Daily Planner, with
                          completion synced both ways. Defaults to today so
                          the field is rarely empty. */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Task Date
                          <span className="ml-1 text-xs font-normal text-gray-400">
                            (shown in Daily Planner)
                          </span>
                        </label>
                        <DateField
                          value={formData.start_date || ''}
                          onChange={(v) => setFormData((prev) => ({ ...prev, start_date: v }))}
                          placeholder="Pick task date"
                        />
                      </div>

                      {/* Minimal Square Calendar Card */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                          Due Date
                        </label>

                        <button
                          type="button"
                          onClick={() => setDueOpen(true)}
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-left flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
                        >
                          <span className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4 text-gray-500" />
                            <span className={dueDate ? "text-gray-900" : "text-gray-400"}>
                              {dueDate ? format(dueDate, "MMM dd, yyyy HH:mm") : "Pick due date"}
                            </span>
                          </span>
                        </button>

                        {dueOpen && createPortal(
                          <div
                            className="fixed inset-0 flex items-center justify-center bg-black/40"
                            style={{ zIndex: 120 }}
                            onClick={() => setDueOpen(false)}
                          >
                            <div
                              className="bg-white rounded-md shadow-md w-[260px]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Calendar
                                mode="single"
                                selected={dueDate || undefined}
                                onSelect={handleDateSelect}
                                className="p-2 [&_.rdp-caption]:mb-0 [&_.rdp-caption_label]:text-[11px] [&_.rdp-nav_button]:w-5 [&_.rdp-nav_button]:h-5 [&_.rdp-day]:w-6 [&_.rdp-day]:h-6 [&_.rdp-day]:text-[11px] [&_.rdp-day_button]:w-6 [&_.rdp-day_button]:h-6 [&_.rdp-head_cell]:w-6 [&_.rdp-head_cell]:text-[10px]"
                              />

                              <div className="flex gap-1 p-2 pt-0 border-t border-gray-100">
                                <input
                                  type="time"
                                  value={dueTime}
                                  onChange={handleTimeChange}
                                  className="flex-1 px-1 py-0.5 text-[11px] border border-gray-300 rounded"
                                />
                                <button
                                  onClick={() => {
                                    setDueDate(null);
                                    setDueTime("");
                                    setFormData((prev) => ({ ...prev, due_date: "" }));
                                    setDueOpen(false);
                                  }}
                                  className="px-2 py-0.5 text-[11px] text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                                >
                                  Clear
                                </button>
                                <button
                                  onClick={() => setDueOpen(false)}
                                  className="px-2 py-0.5 text-[11px] bg-blue-500 text-white rounded hover:bg-blue-600"
                                >
                                  OK
                                </button>
                              </div>
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>

                      {/* Estimated Hours — completes row 2 */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1.5">Estimated Hours</label>
                        <input
                          type="number"
                          name="estimated_hours"
                          value={formData.estimated_hours || ''}
                          onChange={handleChange}
                          step="0.5"
                          min="0"
                          placeholder="e.g., 4, 6.5, 8"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white"
                        />
                      </div>
                    </div>

                    {/* Row 3: Project | Client | Vendor */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                      {/* Project dropdown */}
                      <div>
                        <div className="flex justify-between items-center mb-1.5">
                          <label className="block text-sm font-semibold text-gray-700">Project <span className="text-gray-400 font-normal">(optional)</span></label>
                        </div>
                        <Select
                          value={(formData as any).project_id ?? "none"}
                          onValueChange={(value) => {
                            if (value === "__ADD_NEW_PROJECT__") {
                              setNewProjectForm(f => ({
                                ...f,
                                client_id: (formData as any).client_id || '',
                              }));
                              setShowAddProjectModal(true);
                            } else if (value === "none") {
                              setFormData((prev) => ({
                                ...prev,
                                project_id: null,
                              } as any));
                            } else {
                              const proj = projects.find(p => p.id === value);
                              setFormData((prev) => ({
                                ...prev,
                                project_id: value,
                                ...(proj?.client_id && !(prev as any).client_id ? { client_id: proj.client_id } : {}),
                              } as any));
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Project" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Select Project</SelectItem>
                            {projects
                              .filter(p => !(formData as any).client_id || !p.client_id || p.client_id === (formData as any).client_id)
                              .map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  {p.name}{p.client_name ? ` — ${p.client_name}` : ''}
                                </SelectItem>
                              ))}
                            <SelectItem value="__ADD_NEW_PROJECT__">➕ Add Project</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* In cross-org-draft mode the server resolves
                          client_id automatically from the cross-org link,
                          so the picker is hidden to avoid confusion. */}
                      {mode !== 'cross-org-draft' && (
                        <div>
                          <div className="flex justify-between items-center mb-1.5">
                            <label className="block text-sm font-semibold text-gray-700">Client <span className="text-gray-400 font-normal">(optional)</span></label>
                          </div>
                          <Select
                            value={(formData as any).client_id ?? "none"}
                            onValueChange={(value) => {
                              if (value === "__ADD_NEW__") {
                                setShowAddClientModal(true);
                              } else if (value === "none") {
                                setFormData((prev) => ({
                                  ...prev,
                                  client_id: null,
                                }));
                              } else {
                                setFormData((prev) => ({
                                  ...prev,
                                  client_id: value,
                                }));
                              }
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select Client" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select Client</SelectItem>
                              {clients.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.clientName}
                                </SelectItem>
                              ))}
                              <SelectItem value="__ADD_NEW__">➕ Add Client</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div>
                        <div className="flex justify-between items-center mb-1.5">
                          <label className="block text-sm font-semibold text-gray-700">Vendor <span className="text-gray-400 font-normal">(optional)</span></label>
                        </div>
                        <Select
                          value={(formData as any).vendor_id ?? "none"}
                          onValueChange={(value) => {
                            if (value === "__ADD_NEW__") {
                              setShowAddVendorModal(true);
                            } else if (value === "none") {
                              setFormData((prev) => ({ ...prev, vendor_id: null } as any));
                            } else {
                              setFormData((prev) => ({ ...prev, vendor_id: value } as any));
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Vendor" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Select Vendor</SelectItem>
                            {vendors.map((v) => (
                              <SelectItem key={v.id} value={v.id}>
                                {v.clientName}
                              </SelectItem>
                            ))}
                            <SelectItem value="__ADD_NEW__">➕ Add Vendor</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Vendor group extra members — only shown when a vendor is selected */}
                    {selectedVendor && (
                      <div className="bg-orange-50 rounded-xl p-5 border border-orange-200 mt-2">
                        <div className="mb-3">
                          <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Also add to vendor group
                          </label>
                          <p className="text-xs text-gray-500">
                            Workers above are added to the vendor&apos;s messenger group automatically.
                            Add anyone else who should be in the group (HR, finance, etc.).
                          </p>
                        </div>
                        <div className="space-y-2">
                          {extraGroupMemberIds.map((uid, idx) => (
                            <div key={idx} className="flex gap-2 items-center">
                              <div className="flex-1">
                                <PreparorSelect
                                  value={uid || ""}
                                  options={loadingUsers ? [] : preparorUsers}
                                  onChange={(newId) => {
                                    const next = [...extraGroupMemberIds];
                                    next[idx] = newId;
                                    setExtraGroupMemberIds(next);
                                  }}
                                  disabled={loadingUsers}
                                  placeholder={loadingUsers ? "Loading users…" : "Select person"}
                                />
                              </div>
                              <button
                                type="button"
                                className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                                onClick={() => {
                                  const next = [...extraGroupMemberIds];
                                  next.splice(idx, 1);
                                  setExtraGroupMemberIds(next);
                                }}
                                aria-label="Remove"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="text-sm text-orange-600 hover:text-orange-800 font-medium flex items-center gap-1 mt-1"
                            onClick={() => setExtraGroupMemberIds([...extraGroupMemberIds, ""])}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add to vendor group
                          </button>
                        </div>
                        {extraGroupMemberIds.filter(Boolean).length > 0 && (
                          <div className="mt-3 text-xs text-gray-500">
                            <span className="font-medium">{extraGroupMemberIds.filter(Boolean).length}</span> extra member(s) will be added to the group
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex justify-between pt-4">
                      <button
                        type="button"
                        onClick={() => goToSection('basic')}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-1.5"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          goToSection(hideApprovalTab ? 'checklist' : 'approval')
                        }
                        className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium flex items-center gap-1.5 shadow-md"
                      >
                        {hideApprovalTab ? 'Next: Checklist' : 'Next: Approval Chain'}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Approval Chain Section */}
                {activeSection === 'approval' && (
                  <div className="space-y-3 animate-in fade-in duration-300">
                    {/* Multiple Preparors Selection */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-5 border border-blue-200">
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                              style={{ background: 'linear-gradient(135deg, #60a5fa 0%, #2563eb 100%)', boxShadow: '0 4px 10px -2px rgba(37, 99, 235, 0.45), inset 0 1px 0 rgba(255,255,255,0.35)' }}
                            >1</div>
                            <h3 className="font-semibold text-gray-900">Preparors (Workers)</h3>
                            <span className="text-xs text-red-500">*</span>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">The people who will actually do the work</p>
                        </div>
                        <button
                          type="button"
                          onClick={addPreparor}
                          className="px-3 py-1.5 text-white text-xs rounded-lg transition-colors flex items-center gap-1"
                          style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', boxShadow: '0 6px 14px -4px rgba(37, 99, 235, 0.45), inset 0 1px 0 rgba(255,255,255,0.30)' }}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Preparor
                        </button>
                      </div>

                      <div className="space-y-2">
                        {selectedPreparors.length === 0 ? (
                          <button
                            type="button"
                            onClick={addPreparor}
                            className="w-full py-3 border-2 border-dashed rounded-lg text-sm transition-colors"
                            style={{ borderColor: '#93c5fd', color: '#2563eb', background: '#eff6ff' }}
                          >
                            + Click to add first preparor
                          </button>
                        ) : (
                          selectedPreparors.map((preparorId, idx) => (
                            <div key={idx} className="flex gap-2 items-center group">
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium"
                                style={{ background: '#dbeafe', color: '#1d4ed8' }}
                              >
                                {idx + 1}
                              </div>
                              <div className="flex-1">
                                <PreparorSelect
                                  value={preparorId || ""}
                                  onChange={(v) => updatePreparor(idx, v)}
                                  options={loadingUsers ? [] : preparorUsers}
                                  disabled={loadingUsers}
                                  placeholder={loadingUsers ? "Loading users…" : "Select Preparor"}
                                />
                              </div>
                              {selectedPreparors.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removePreparor(idx)}
                                  className="opacity-0 group-hover:opacity-100 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          ))
                        )}
                      </div>

                      {selectedPreparors.length > 0 && (
                        <div className="mt-3 text-xs text-gray-500">
                          <span className="font-medium">{selectedPreparors.filter(p => p).length}</span> preparor(s) selected
                        </div>
                      )}
                    </div>

                    {/* Approval Chain Visual Flow */}
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                      <div className="flex justify-between items-center mb-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold"
                              style={{ background: 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)', boxShadow: '0 4px 10px -2px rgba(234, 88, 12, 0.45), inset 0 1px 0 rgba(255,255,255,0.35)' }}
                            >2</div>
                            <h3 className="font-semibold text-gray-900">Approval Chain</h3>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Set who reviews this task in order</p>
                        </div>
                        <button
                          type="button"
                          onClick={addApprovalLevel}
                          className="px-3 py-1.5 text-white text-xs rounded-lg transition-colors flex items-center gap-1"
                          style={{ background: 'linear-gradient(135deg, #fb923c 0%, #ea580c 100%)', boxShadow: '0 6px 14px -4px rgba(234, 88, 12, 0.45), inset 0 1px 0 rgba(255,255,255,0.30)' }}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          Add Level
                        </button>
                      </div>

                      {/* Visual Flow Diagram */}
                      <div className="flex items-center gap-1 mb-5 p-3 bg-white rounded-lg border border-gray-200 overflow-x-auto">
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div
                            className="w-12 h-12 rounded-full flex items-center justify-center"
                            style={{ background: '#dbeafe', border: '2px solid #2563eb' }}
                          >
                            <svg className="w-6 h-6" fill="none" stroke="#1d4ed8" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                          </div>
                          <span className="text-xs font-semibold mt-1" style={{ color: '#1d4ed8' }}>PREPARORS</span>
                          <span className="text-xs text-gray-400">{selectedPreparors.filter(p => p).length} Worker(s)</span>
                        </div>

                        {(formData.approvalChain || []).map((level, idx) => (
                          <div key={idx} className="flex items-center">
                            <div className="w-8 h-0.5" style={{ background: '#cbd5e1' }} />
                            <div className="flex flex-col items-center flex-shrink-0">
                              <div
                                className="w-10 h-10 rounded-full flex items-center justify-center"
                                style={{ background: '#fed7aa', border: '2px solid #ea580c' }}
                              >
                                <span className="text-sm font-bold" style={{ color: '#c2410c' }}>L{level.level}</span>
                              </div>
                              <span className="text-xs font-semibold mt-1 max-w-[60px] text-center truncate" style={{ color: '#c2410c' }}>
                                {level.roleLabel || `Reviewer`}
                              </span>
                            </div>
                          </div>
                        ))}

                        <div className="w-8 h-0.5" style={{ background: '#cbd5e1' }} />
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ background: '#bbf7d0', border: '2px solid #10b981' }}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="#047857" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                          <span className="text-xs font-semibold mt-1" style={{ color: '#047857' }}>DONE</span>
                        </div>
                      </div>

                      {/* Approval Levels List */}
                      <div className="space-y-3">
                        {(formData.approvalChain || []).map((level, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div
                                  className="w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center"
                                  style={{ background: '#fed7aa', color: '#c2410c' }}
                                >
                                  {level.level}
                                </div>
                                <span className="font-medium text-gray-700">Level {level.level} Approval</span>
                              </div>
                              {(formData.approvalChain?.length || 0) > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeApprovalLevel(idx)}
                                  className="text-red-500 hover:text-red-700 text-xs px-2 py-1 rounded hover:bg-red-50"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Who Will Approve</label>
                                {/* Same typeahead picker the Preparor list uses
                                    — type to filter, Enter selects the
                                    highlighted match. Level 1 is seeded with
                                    the task creator by buildInitialForm; the
                                    user can still swap it from here. */}
                                <PreparorSelect
                                  value={level.approver || ""}
                                  onChange={(v) => updateApprovalLevel(idx, 'approver', v)}
                                  options={loadingUsers ? [] : approverUsers}
                                  disabled={loadingUsers}
                                  placeholder={loadingUsers ? "Loading users…" : "Select reviewer"}
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Role Label</label>
                                <input
                                  type="text"
                                  value={level.roleLabel}
                                  onChange={(e) => updateApprovalLevel(idx, 'roleLabel', e.target.value)}
                                  placeholder="e.g., Accounts Reviewer"
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-between pt-4">
                      <button
                        type="button"
                        onClick={() => goToSection('details')}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-1.5"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={() => goToSection('checklist')}
                        className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium flex items-center gap-1.5 shadow-md"
                      >
                        Next: Checklist
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                {/* Checklist Section */}
                {activeSection === 'checklist' && (
                  <div className="space-y-3 animate-in fade-in duration-300">
                    <div>
                      <div className="flex justify-between items-end mb-2 gap-3">
                        <label className="block text-sm font-semibold text-gray-700">Checklist Items</label>
                        <div className="flex items-center gap-3">
                          <ErrorBoundary inline name="ai-generate-checklist">
                            <AiGenerateButton
                              idleLabel="Generate with AI"
                              disabled={!formData.title.trim() && !(formData.description || '').trim()}
                              disabledHint="Add a title or description first"
                              run={async () => {
                                const r = await generateTaskChecklist(formData.title || '', formData.description || '');
                                if (!r.ok) return r.message || 'AI is unavailable right now.';
                                const generated = (r.items || []).map(s => s.trim()).filter(Boolean);
                                if (!generated.length) return 'No checklist items generated. Try again.';
                                setFormData(prev => {
                                  const existing = (prev.checklist || []).filter(s => s.trim());
                                  return { ...prev, checklist: existing.length ? [...existing, ...generated] : generated };
                                });
                                return null;
                              }}
                            />
                          </ErrorBoundary>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {(formData.checklist || []).filter(i => i.trim()).length} items
                          </span>
                        </div>
                      </div>
                      <div
                        ref={checklistRef}
                        className="space-y-2 max-h-64 overflow-y-auto pr-2"
                      >
                        {(formData.checklist || []).map((item, index) => (
                          <div key={index} className="flex gap-2 items-center group">
                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                              {index + 1}
                            </div>
                            <input
                              type="text"
                              value={item}
                              onChange={(e) => handleChecklistChange(index, e.target.value)}
                              placeholder={`Task item ${index + 1}`}
                              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            />
                            {(formData.checklist || []).length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeChecklistItem(index)}
                                className="opacity-0 group-hover:opacity-100 p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={addChecklistItem}
                        className="mt-3 text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Checklist Item
                      </button>
                    </div>

                    {/* Summary Card */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                      <h4 className="font-semibold text-gray-800 text-sm mb-2">📋 Task Summary</h4>
                      <div className="space-y-1 text-xs text-gray-600">
                        <p><strong>Title:</strong> {formData.title || 'Not set'}</p>
                        <p><strong>Preparors:</strong> {
                          selectedPreparors.filter(p => p).length > 0
                            ? selectedPreparors.filter(p => p).map(p => getUserName(p)).join(', ')
                            : 'Not set'
                        }</p>
                        <p><strong>Approval Levels:</strong> {formData.approvalChain?.length || 0}</p>
                        <p><strong>Checklist Items:</strong> {(formData.checklist || []).filter(i => i.trim()).length}</p>
                      </div>
                    </div>

                    <div className="flex justify-between pt-4">
                      <button
                        type="button"
                        onClick={() =>
                          goToSection(hideApprovalTab ? 'details' : 'approval')
                        }
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-1.5"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => goToSection('attachments')}
                          className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all font-medium flex items-center gap-1.5 shadow-md"
                        >
                          Next: Attachments
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Attachments Section — files staged here and uploaded after
                    the task is created so they can be linked to its new id. */}
                {activeSection === 'attachments' && (
                  <div className="space-y-3 animate-in fade-in duration-300">
                    <div
                      ref={attachmentDropRef}
                      tabIndex={-1}
                      className={`outline-none rounded-lg transition-all ${
                        attachmentDragOver ? 'ring-2 ring-blue-400 ring-offset-2 bg-blue-50/40 p-3' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-semibold text-gray-700">Attachments</label>
                        <span className="text-xs text-gray-400">
                          {stagedAttachments.length} file{stagedAttachments.length === 1 ? '' : 's'} staged
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        Drag &amp; drop, paste with Ctrl/Cmd+V, or click below. They'll upload after the task is created. Max 5 MB per file.
                      </p>

                      <input
                        ref={attachmentInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain"
                        onChange={(e) => {
                          acceptAttachmentFiles(Array.from(e.target.files || []));
                          if (e.target) e.target.value = '';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => attachmentInputRef.current?.click()}
                        className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Attachment
                      </button>

                      {stagedAttachments.length > 0 && (
                        <ul className="mt-4 space-y-2">
                          {stagedAttachments.map((f, idx) => (
                            <li
                              key={`${f.name}-${idx}`}
                              className="flex items-center justify-between gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg"
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                {/* Inline preview — image thumbnail / PDF
                                    badge / generic file icon — matches the
                                    DocUploadCard pattern in Employee form
                                    so the staged file is visible before
                                    the task is even created. */}
                                <AttachmentThumb file={f} />
                                <div className="min-w-0">
                                  <p className="text-sm text-gray-800 truncate">{f.name}</p>
                                  <p className="text-[11px] text-gray-500">{(f.size / 1024).toFixed(1)} KB</p>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => setStagedAttachments(prev => prev.filter((_, i) => i !== idx))}
                                disabled={uploadingAttachments}
                                className="text-red-600 hover:text-red-700 text-xs font-medium disabled:opacity-50"
                              >
                                Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="flex justify-between pt-4">
                      <button
                        type="button"
                        onClick={() => goToSection('checklist')}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium flex items-center gap-1.5"
                        disabled={uploadingAttachments || loading}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Back
                      </button>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={handleClose}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                          disabled={uploadingAttachments || loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={
                            loading
                            || uploadingAttachments
                            || !isBasicValid
                            // Preparor is required for in-tenant tasks only. In
                            // cross-org-draft mode the receiving org's admin
                            // assigns the worker when promoting the draft, so
                            // the originator doesn't need to pick one.
                            || (
                              effectiveMode !== 'cross-org-draft'
                              && (selectedPreparors.length === 0 || selectedPreparors.every(p => !p))
                            )
                          }
                          className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-1.5 shadow-md"
                        >
                          {loading || uploadingAttachments ? (
                            <>
                              <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              {uploadingAttachments ? 'Uploading attachments…' : 'Creating…'}
                            </>
                          ) : (
                            <>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Create Task
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Admin-review footer — always visible (sits below scrollable body),
                so admins can Approve / Send Back / Reject from any section. */}
            {mode === 'admin-review' && (
              <div
                className="shrink-0 px-4 md:px-6 py-3 flex flex-wrap justify-end gap-2"
                style={{ borderTop: '1px solid var(--gt-divider)', background: 'var(--gt-surface-0)' }}
              >
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setReasonModal('reject')}
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Reject
                </button>
                <button
                  type="button"
                  onClick={() => setReasonModal('send-back')}
                  className="px-3 py-1.5 text-sm bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors font-medium"
                >
                  Send Back
                </button>
                <button
                  type="button"
                  onClick={handleApprove}
                  className="px-3.5 py-1.5 text-sm bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg hover:from-green-600 hover:to-emerald-700 transition-all font-medium shadow-md"
                >
                  Approve & Assign
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Send Back / Reject reason modal (admin-review).
          Rendered INLINE (no nested portal) at z-[120] — same pattern as the
          Create Task Type Modal below which is known to work. Nested
          createPortal was visually swallowed in some React/router setups. */}
      {reasonModal && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[120] transition-opacity duration-300"
            onClick={() => { setReasonModal(null); setReasonText(''); }}
          />
          <div className="fixed inset-0 z-[120] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-300"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-900">
                    {reasonModal === 'send-back' ? 'Send Back to Originator' : 'Reject Draft'}
                  </h3>
                  <label htmlFor="reason-textarea" className="block text-sm mb-1 font-medium text-gray-700">
                    Reason
                  </label>
                  <textarea
                    id="reason-textarea"
                    value={reasonText}
                    onChange={(e) => setReasonText(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2 h-24 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                    placeholder={
                      reasonModal === 'send-back'
                        ? 'Explain what the originator needs to change…'
                        : 'Explain why you are rejecting this draft…'
                    }
                    autoFocus
                  />
                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      type="button"
                      onClick={() => { setReasonModal(null); setReasonText(''); }}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={submitReason}
                      disabled={!reasonText.trim()}
                      className={`px-4 py-2 text-sm rounded-lg font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                        reasonModal === 'send-back'
                          ? 'bg-amber-500 hover:bg-amber-600'
                          : 'bg-red-600 hover:bg-red-700'
                      }`}
                    >
                      {reasonModal === 'send-back' ? 'Send Back' : 'Reject'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Create Task Type Modal */}
      {showCreateTaskTypeModal && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] transition-opacity duration-300"
            onClick={() => setShowCreateTaskTypeModal(false)}
          />
          <div className="fixed inset-0 z-[110] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-300"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l5 5a2 2 0 01.586 1.414V19a2 2 0 01-2 2H7a2 2 0 01-2-2V5a2 2 0 012-2z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Create Task Type</h3>
                    </div>
                    <button
                      onClick={() => setShowCreateTaskTypeModal(false)}
                      className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Type Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newTaskTypeName}
                        onChange={(e) => setNewTaskTypeName(e.target.value)}
                        placeholder="e.g., Documentation, Research, Testing"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleCreateTaskType();
                          }
                        }}
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowCreateTaskTypeModal(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleCreateTaskType}
                        disabled={creatingTaskType || !newTaskTypeName.trim()}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 shadow-md"
                      >
                        {creatingTaskType ? (
                          <>
                            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Creating...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Create Type
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Task Type Modal */}
      {showEditModal && editingType && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] transition-opacity duration-300"
            onClick={() => setShowEditModal(false)}
          />
          <div className="fixed inset-0 z-[110] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-300"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-md">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Edit Task Type</h3>
                    </div>
                    <button
                      onClick={() => setShowEditModal(false)}
                      className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                        Type Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="e.g., Documentation, Research, Testing"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleUpdateTaskType();
                          }
                        }}
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowEditModal(false)}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleUpdateTaskType}
                        disabled={creatingTaskType || !editName.trim()}
                        className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 shadow-md"
                      >
                        {creatingTaskType ? (
                          <>
                            <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Updating...
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Update Type
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteType && (
        <>
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] transition-opacity duration-300"
            onClick={() => setShowDeleteModal(false)}
          />
          <div className="fixed inset-0 z-[110] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <div
                className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-300"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Delete Task Type</h3>
                      <p className="text-sm text-gray-500">This action cannot be undone</p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <p className="text-gray-700">
                      Are you sure you want to delete "<span className="font-semibold">{deleteType.name}</span>"?
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Tasks using this type will not be affected, but you won't be able to select this type for new tasks.
                    </p>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setShowDeleteModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleDeleteTaskType}
                      disabled={loading}
                      className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Deleting...
                        </>
                      ) : (
                        'Delete Type'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoomIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-in {
          animation-duration: 0.3s;
          animation-fill-mode: both;
        }
        .fade-in {
          animation-name: fadeIn;
        }
        .zoom-in {
          animation-name: zoomIn;
        }
      `}</style>

      {/* Quick Add Client Modal */}
      {showAddClientModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={closeClientModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white z-10 p-5 border-b border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0M12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Add New Client</h3>
                <p className="text-xs text-gray-500">Quick add — full details in Client panel</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              {/* GSTIN auto-fill hint */}
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100 text-xs text-indigo-800">
                <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" /></svg>
                <span><strong className="font-semibold">Tip:</strong> Got a GSTIN? Type it below and hit <em>Fetch</em> — name, address, state, PAN, country and pincode will auto-fill from the GST registry.</span>
              </div>

              {/* GSTIN + PAN — moved to the top of the form so auto-fill happens before anything else */}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                  <input type="text" value={newClientForm.gstin}
                    onChange={e => { setNewClientForm(f => ({ ...f, gstin: e.target.value.toUpperCase() })); setGstinLookupClientMsg(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupGstinFor('client', newClientForm.gstin); } }}
                    placeholder="22AAAAA0000A1Z5" maxLength={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-mono"
                    autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                  <input type="text" value={newClientForm.pan} onChange={e => setNewClientForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))}
                    placeholder="AAAAA0000A" maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-mono" />
                </div>
                <button type="button"
                  onClick={() => lookupGstinFor('client', newClientForm.gstin)}
                  disabled={gstinLookingUpClient || newClientForm.gstin.trim().length !== 15}
                  className="h-[38px] px-4 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {gstinLookingUpClient ? 'Fetching…' : 'Fetch'}
                </button>
              </div>
              {gstinLookupClientMsg && (
                <div className={`text-xs px-3 py-1.5 rounded-md ${gstinLookupClientMsg.kind === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'}`}>
                  {gstinLookupClientMsg.text}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client Name <span className="text-red-500">*</span></label>
                <input type="text" value={newClientForm.clientName} onChange={e => setNewClientForm(f => ({ ...f, clientName: e.target.value }))}
                  placeholder="e.g., Acme Corporation" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={newClientForm.email} onChange={e => setNewClientForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="email@co.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={newClientForm.phone} onChange={e => setNewClientForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+91..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <input type="text" value={newClientForm.address1} onChange={e => setNewClientForm(f => ({ ...f, address1: e.target.value }))}
                  placeholder="Street address" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                <input type="text" value={newClientForm.address2} onChange={e => setNewClientForm(f => ({ ...f, address2: e.target.value }))}
                  placeholder="Area, landmark…" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input type="text" value={newClientForm.state} onChange={e => setNewClientForm(f => ({ ...f, state: e.target.value }))}
                    placeholder="State" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input type="text" value={newClientForm.country} onChange={e => setNewClientForm(f => ({ ...f, country: e.target.value }))}
                    placeholder="Country" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={newClientForm.pincode}
                    onChange={e =>
                      setNewClientForm(f => ({
                        ...f,
                        pincode: e.target.value.replace(/\D+/g, '').slice(0, 6),
                      }))
                    }
                    placeholder="6-digit pincode"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>
              </div>
              <div className="pt-4 mt-2 border-t border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-800">Branches</h4>
                    <p className="text-[11px] text-gray-500">
                      Optional. Add one row per branch address.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addClientBranch}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold"
                  >
                    + Add Branch
                  </button>
                </div>
                {newClientForm.branchDetails.length === 0 ? (
                  <p className="text-[11px] text-gray-400 italic">No branches added yet.</p>
                ) : (
                  <div className="space-y-3">
                    {newClientForm.branchDetails.map((branch, idx) => (
                      <div
                        key={idx}
                        className="relative bg-gray-50 border border-gray-200 rounded-lg p-3"
                      >
                        <div className="absolute -top-2 left-3 bg-indigo-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                          Branch {idx + 1}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeClientBranch(idx)}
                          aria-label="Remove branch"
                          className="absolute top-2 right-2 p-1 text-gray-400 hover:text-red-500"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                          <div className="sm:col-span-2">
                            <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">
                              Branch Name
                            </label>
                            <input
                              type="text"
                              value={branch.branchName}
                              onChange={e => updateClientBranch(idx, 'branchName', e.target.value)}
                              placeholder="e.g. Mumbai Office"
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">
                              GSTIN
                            </label>
                            <div className="flex gap-1.5">
                              <input
                                type="text"
                                value={branch.gstin}
                                maxLength={15}
                                onChange={e => updateClientBranch(idx, 'gstin', e.target.value.toUpperCase())}
                                placeholder="22AAAAA0000A1Z5"
                                className="flex-1 min-w-0 px-2.5 py-1.5 border border-gray-300 rounded-md text-xs font-mono focus:ring-2 focus:ring-indigo-500"
                              />
                              {/* Fires on mousedown so a slight mouse drift
                                  between press and release can't cancel the
                                  click — same pattern used on the main
                                  GSTIN field. */}
                              <button
                                type="button"
                                disabled={
                                  clientBranchLookupIdx === idx ||
                                  branch.gstin.trim().length !== 15
                                }
                                onMouseDown={e => {
                                  e.preventDefault();
                                  if (e.button !== 0) return;
                                  if (
                                    clientBranchLookupIdx === idx ||
                                    branch.gstin.trim().length !== 15
                                  ) return;
                                  lookupGstinForBranch(idx, branch.gstin);
                                }}
                                onClick={e => e.preventDefault()}
                                className="px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100 active:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                              >
                                {clientBranchLookupIdx === idx ? 'Fetching…' : 'Fetch'}
                              </button>
                            </div>
                            {clientBranchLookupMsg?.idx === idx && (
                              <div
                                className={`mt-1 text-[10px] px-2 py-1 rounded ${
                                  clientBranchLookupMsg.kind === 'ok'
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                                    : 'bg-rose-50 text-rose-700 border border-rose-100'
                                }`}
                              >
                                {clientBranchLookupMsg.text}
                              </div>
                            )}
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">
                              Pincode
                            </label>
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              value={branch.pincode}
                              onChange={e =>
                                updateClientBranch(
                                  idx,
                                  'pincode',
                                  e.target.value.replace(/\D+/g, '').slice(0, 6),
                                )
                              }
                              placeholder="6-digit"
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">
                              Address Line 1
                            </label>
                            <input
                              type="text"
                              value={branch.address1}
                              onChange={e => updateClientBranch(idx, 'address1', e.target.value)}
                              placeholder="Street"
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">
                              Address Line 2
                            </label>
                            <input
                              type="text"
                              value={branch.address2}
                              onChange={e => updateClientBranch(idx, 'address2', e.target.value)}
                              placeholder="Area, landmark…"
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">
                              State
                            </label>
                            <input
                              type="text"
                              value={branch.state}
                              onChange={e => updateClientBranch(idx, 'state', e.target.value)}
                              placeholder="State"
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                          <div>
                            <label className="block text-[11px] font-semibold text-gray-700 mb-0.5">
                              Country
                            </label>
                            <input
                              type="text"
                              value={branch.country}
                              onChange={e => updateClientBranch(idx, 'country', e.target.value)}
                              placeholder="Country"
                              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs focus:ring-2 focus:ring-indigo-500"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-white p-5 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={closeClientModal} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancel</button>
              <button onClick={handleSaveNewClient} disabled={savingClient || !newClientForm.clientName.trim()}
                className="px-5 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all text-sm font-medium disabled:opacity-50">
                {savingClient ? 'Adding…' : 'Add Client'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Vendor Modal — mirrors the client modal exactly, just bound
          to vendor state + vendorsApi. */}
      {showAddVendorModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={() => { setShowAddVendorModal(false); setGstinLookupVendorMsg(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white z-10 p-5 border-b border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Add New Vendor</h3>
                <p className="text-xs text-gray-500">Quick add — full details in Vendor panel</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-800">
                <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" /></svg>
                <span><strong className="font-semibold">Tip:</strong> Got a GSTIN? Type it below and hit <em>Fetch</em> — name, address, state, PAN, country and pincode will auto-fill from the GST registry.</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">GSTIN</label>
                  <input type="text" value={newVendorForm.gstin}
                    onChange={e => { setNewVendorForm(f => ({ ...f, gstin: e.target.value.toUpperCase() })); setGstinLookupVendorMsg(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookupGstinFor('vendor', newVendorForm.gstin); } }}
                    placeholder="22AAAAA0000A1Z5" maxLength={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm font-mono"
                    autoFocus />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN</label>
                  <input type="text" value={newVendorForm.pan} onChange={e => setNewVendorForm(f => ({ ...f, pan: e.target.value.toUpperCase() }))}
                    placeholder="AAAAA0000A" maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm font-mono" />
                </div>
                <button type="button"
                  onClick={() => lookupGstinFor('vendor', newVendorForm.gstin)}
                  disabled={gstinLookingUpVendor || newVendorForm.gstin.trim().length !== 15}
                  className="h-[38px] px-4 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                  {gstinLookingUpVendor ? 'Fetching…' : 'Fetch'}
                </button>
              </div>
              {gstinLookupVendorMsg && (
                <div className={`text-xs px-3 py-1.5 rounded-md ${gstinLookupVendorMsg.kind === 'ok' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'}`}>
                  {gstinLookupVendorMsg.text}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vendor Name <span className="text-red-500">*</span></label>
                <input type="text" value={newVendorForm.clientName} onChange={e => setNewVendorForm(f => ({ ...f, clientName: e.target.value }))}
                  placeholder="e.g., Bharat Supplies Pvt Ltd" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input type="email" value={newVendorForm.email} onChange={e => setNewVendorForm(f => ({ ...f, email: e.target.value }))}
                    placeholder="email@co.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input type="tel" value={newVendorForm.phone} onChange={e => setNewVendorForm(f => ({ ...f, phone: e.target.value }))}
                    placeholder="+91..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <input type="text" value={newVendorForm.address1} onChange={e => setNewVendorForm(f => ({ ...f, address1: e.target.value }))}
                  placeholder="Street address" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                <input type="text" value={newVendorForm.address2} onChange={e => setNewVendorForm(f => ({ ...f, address2: e.target.value }))}
                  placeholder="Area, landmark…" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input type="text" value={newVendorForm.state} onChange={e => setNewVendorForm(f => ({ ...f, state: e.target.value }))}
                    placeholder="State" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  <input type="text" value={newVendorForm.country} onChange={e => setNewVendorForm(f => ({ ...f, country: e.target.value }))}
                    placeholder="Country" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input type="text" value={newVendorForm.pincode} onChange={e => setNewVendorForm(f => ({ ...f, pincode: e.target.value }))}
                    placeholder="000000" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm" />
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white p-5 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={() => { setShowAddVendorModal(false); setGstinLookupVendorMsg(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancel</button>
              <button onClick={handleSaveNewVendor} disabled={savingVendor || !newVendorForm.clientName.trim()}
                className="px-5 py-2 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:from-amber-700 hover:to-orange-700 transition-all text-sm font-medium disabled:opacity-50">
                {savingVendor ? 'Adding…' : 'Add Vendor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Project Modal */}
      {showAddProjectModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[110] flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={() => setShowAddProjectModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-gray-100 flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Add New Project</h3>
                <p className="text-xs text-gray-500">Quick add — manage details in Projects panel</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name <span className="text-red-500">*</span></label>
                <input type="text" value={newProjectForm.name} onChange={e => setNewProjectForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g., Website Redesign" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" autoFocus />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Client (optional)</label>
                <Select
                  value={newProjectForm.client_id || "__none__"}
                  onValueChange={(v) => setNewProjectForm(f => ({ ...f, client_id: v === "__none__" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Client" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— No client —</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.clientName}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={newProjectForm.description} onChange={e => setNewProjectForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} placeholder="Brief description..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-3 justify-end">
              <button onClick={() => setShowAddProjectModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancel</button>
              <button onClick={handleSaveNewProject} disabled={savingProject || !newProjectForm.name.trim()}
                className="px-5 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all text-sm font-medium disabled:opacity-50">
                {savingProject ? 'Adding…' : 'Add Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

export default TaskCreate;
